import { isRelativePath } from '../../scripts/generateTranslationBundles/utils/isRelativePath';
import { buildUri, CLOUDFRONT_ENDPOINT } from '../endpoints';
import { DEFAULT_LOCALE_CODE, LOCALE_CODE_TO_CHAMELEON_LANGUAGE } from '../const';
import { getCourseCurriculumLocaleData } from './getCourseCurriculumLocaleData';
import { LearningObjectUtils } from './LearningObjectUtils';
import { CategoryUtils } from './CategoryUtils';
import { authService } from '../services/authService';

/**
 * A utility class useful for helping to determine things about a particular
 * course. Use of this class is entirely optional.
 */
export class CourseUtils {

    /**
     * The possible types a course may fall under.
     */
    static types = {
        VIDEO: {
            type: 'video',
            color: '#b359d4',
        },
        SCORM: {
            type: 'scorm',
            color: '#0090cf',
        },
        UNKNOWN: {
            type: 'unknown',
            color: '#b359d4',
        },
    };

    /**
     * Returns the type the course falls under.
     * 
     * @param { object } courseData - The course data.
     * @returns { { type: string, color: string } }
     */
    static getType( courseData ) {
        const learningObjects = courseData?.learningObjects ? courseData.learningObjects : [];
        const hasLearningObjects = ( learningObjects.length > 0 );
        if ( hasLearningObjects ) {
            // Check if all of the learning objects are considered to be videos.
            const areVideos = learningObjects.every( learningObject => LearningObjectUtils.isVideo( learningObject ) );
            if ( areVideos ) {
                return this.types.VIDEO;
            } else {
                return this.types.SCORM;
            }
        } else {
            return this.types.UNKNOWN;
        }
    }

    static localeToChameleonLanguage( locale ) {
        const result = LOCALE_CODE_TO_CHAMELEON_LANGUAGE[ locale ] || 'en-US';
        console.log( `Mapping user's locale ${ locale } to chameleon locale ${ result }` );
        return result;
    }

    /**
     * Retrieves the progress percentage from the provided course.
     * 
     * @param { object } courseData - The data of the course.
     * @returns { number }
     */
    static getProgressPercentage( courseData ) {

        if ( courseData?.enrollment ) {
            return courseData.enrollment.percentComplete == 100 || courseData.enrollment.status == 'COMPLETED' ? 100
                : courseData.enrollment.status == 'IN_PROGRESS' ? 50
                : 0.0;
        }

        const learningObjects = courseData?.learningObjects;
        let finalPercentage = 0.0;

        if ( learningObjects ) {
            for ( let i = 0; i < learningObjects.length; i++ ) {
                const object = learningObjects[ i ];

                const isComplete = LearningObjectUtils.isComplete( object );
                if ( isComplete ) {
                    finalPercentage += 100;
                    continue;
                }

                const inProgress = LearningObjectUtils.isInProgress( object );
                if ( inProgress ) {
                    // We've decided on using 0.5 (50%) to represent a course/item "in-progress". This
                    // will probably change in the future once we can get a more accurate percentage.
                    finalPercentage += 50;
                    continue;
                }
            }

            finalPercentage /= learningObjects.length;
        }

        return finalPercentage;
    }

    /**
     * Retrieves the ID of the curriculum.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getId( courseData ) {
        return courseData?.id;
    }

    /**
     * Retrieves the thumbnail image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getThumbnailImage( courseData ) {
        return courseData?.thumbnailImage;
    }

    /**
     * Retrieves the URL of the thumbnail image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getThumbnailImageUrl( courseData, { forceAbsolutePath = true } = {} ) {
        const path = this.getThumbnailImage( courseData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_ENDPOINT, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the hero image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getHeroImage( courseData ) {
        return courseData?.heroImage;
    }

    /**
     * Retrieves the URL of the hero image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getHeroImageUrl( courseData, { forceAbsolutePath = true } = {} ) {
        const path = this.getHeroImage( courseData );
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_ENDPOINT, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Returns the locale data for the course. If there happens to be locale data stored
     * in the course's metadata too, that will also be returned. If there are duplicate
     * locales between the official locales and the locales found in the metadata,
     * prioritize the official locale data.
     * 
     * @param { object } courseData - The data of the course.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getLocales( courseData ) {
        return getCourseCurriculumLocaleData( courseData );
    }

    /**
     * Retrieves the name of the curriculum in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code
     * of `en-US` will be used.
     * 
     * @param { object } courseData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getName( courseData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {
        // Validate the locale code parameter.
        if ( !localeCode ) {
            localeCode = DEFAULT_LOCALE_CODE;
        }

        const locales = this.getLocales( courseData );
        let translatedName = locales?.find( ( data ) => ( data?.locale == localeCode ) )?.name;

        // If no translated name is found and fallbacks are allowed, attempt to find a
        // translated name in the site's default language.
        if ( !translatedName && allowFallback && localeCode != DEFAULT_LOCALE_CODE ) {
            translatedName = this.getName( courseData, { localeCode: DEFAULT_LOCALE_CODE, allowFallback: false } );
        }

        return translatedName;
    }

    /**
     * Retrieves the description of the curriculum in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code of
     * `en-US` will be used.
     * 
     * @param { object } courseData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getDescription( courseData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {
        // Validate the locale code parameter.
        if ( !localeCode ) {
            localeCode = DEFAULT_LOCALE_CODE;
        }

        const locales = this.getLocales( courseData );
        let translatedDescription = locales?.find( ( data ) => ( data?.locale == localeCode ) )?.description;

        // If no translated name is found and fallbacks are allowed, attempt to find a
        // translated name in the site's default language.
        if ( !translatedDescription && allowFallback && localeCode != DEFAULT_LOCALE_CODE ) {
            translatedDescription = this.getDescription( courseData, { localeCode: DEFAULT_LOCALE_CODE, allowFallback: false } );
        }

        return translatedDescription;
    }

    /**
     * Returns the category IDs assigned to the provided course.
     * 
     * @param { object } courseData - The course data.
     * @returns { string[] }
     */
    static getCategoryIds( courseData ) {
        return courseData?.categoryIds;
    }

    /**
     * Returns the brand IDs assigned to the provided course.
     * 
     * @param { object } courseData - The course data.
     * @returns { string[] }
     */
    static getBrandIds( courseData ) {
        return courseData?.brandIds;
    }

    /**
     * Returns the company relationship IDs assigned to the course
     * 
     * @param {object} courseData - The course data.
     * @returns { string[] } company relationship UUIDs
     */
    static getCompanyRelationshipIds( courseData ) {
        return courseData?.companyRelationshipIds;
    }

    /**
     * Returns the locale IDs assigned to the provided course
     * @param {object} courseData - the course data
     * @returns { string[] }
     */
    static getLocaleIds( courseData ) {
        return courseData?.localeIds ?? [];
    }

    /**
     * Returns the product IDs assigned to the provided course
     * @param {object} courseData - the course data
     * @returns { string[] }
     */
    static getProductIds( courseData ) {
        return courseData?.productIds ?? [];
    }

    /**
     * Filters a list of courses to only those that are available to the provided brands.
     * 
     * @param { object | object[] } courseData - The objects(s) to filter.
     * @param { string | string[] } brandIds - The brand IDs to use to filter content.
     */
    static filterByBrands( courseData, brandIds ) {
        const courseList = ( courseData ) ? ( !Array.isArray( courseData ) ) ? [ courseData ] : courseData : [];
        const brandIdList = ( brandIds ) ? ( !Array.isArray( brandIds ) ) ? [ brandIds ] : brandIds : [];

        return courseList.filter( ( course ) => {
            const courseBrandIds = this.getBrandIds( course );

            // Require at least one brand to exist on the course.
            const containsRequiredBrands = courseBrandIds?.some( ( courseBrandId ) => brandIdList.includes( courseBrandId ) );
            return containsRequiredBrands;
        } );
    }

    /**
     * Filters a list of courses to only those that are available to the provided segments. 
     * 
     * @param { object | object[] } courseData - The objects(s) to filter.
     * @param { string[] } brandIds - The brand IDs to use to filter content.
     * @param { string[] } userGroupIds - The group IDs to use to filter content.
     * @returns { object[] }
     */
    static filterByBrandsAndGroups( courseData, brandIds, userGroupIds ) {
        const userTags = authService.getTags() || [];

        const objectList = ( courseData )
            ? ( !Array.isArray( courseData ) ) ? [ courseData ] : courseData
            : [];

        // Determine the filter function to use depending on the provided segment data.

        return objectList.filter( courseNode => courseNode
            && this.getBrandIds( courseNode )?.some( brandId => brandIds?.includes( brandId ) )
            && this.getCompanyRelationshipIds( courseNode )?.some( crId => userGroupIds?.includes( crId ) )
            && ( userTags.includes( 'employee' ) || userTags.includes( 'miyosmart' ) || !this.getProductIds( courseNode ).some( productId => productId ) )
            && this.getLocaleIds( courseNode )?.some( localeId => userGroupIds?.includes( localeId ) ) );
    }

    /**
     * Returns only the categories that are connected to the provided course data.
     * 
     * @param { object } courseData - The course data to read.
     * @param { object[] } categoryDataList - The category list to look through for connections.
     * @returns { object[] }
     */
    static getConnectedCategories( courseData, categoryDataList ) {
        const courseCategoryIds = CourseUtils.getCategoryIds( courseData ) || [];
        return ( categoryDataList || [] ).filter( ( categoryData ) => {
            return courseCategoryIds.includes( CategoryUtils.getId( categoryData ) );
        } );
    }

}
